import {
  addTemporaryIntercomGlobal,
  addIntercomScript,
} from './analytics/intercom-snippet';
import get from 'lodash.get';
import { READY_GATE } from '@/services/bootstrap';
import { ORPHANED_USER_ORG } from '@/services/google-analytics';

export const userAttributes = {
  npsLastScore: 'NPS Last Score',
  npsLastComment: 'NPS Last Comment',
  npsLastResponseDate: 'NPS Last Response Date',
  npsLastDeclineDate: 'NPS Last Decline Date',
  npsLastUrl: 'NPS Last URL',
  npsLastApp: 'NPS Last App',
};

export default function intercomFactory(services) {
  // Apps to not report view events to Intercom (internal apps, reduces unnecessary events)
  const VIEW_EVENT_APP_EXCLUSIONS = [
    'market-admin',
    'analytics-portal',
    'admin',
  ];
  const NUM_UPDATES_THROTTLE_THRESHOLD = 10;
  let numUpdates = 0;
  let intercomConfig;

  let thisService = {
    init,
    hideMessenger,
    showMessenger,
    update,
    eventTrack,
  };
  activate();
  return thisService;

  ////////////////////

  function activate() {
    // Create the temporary intercom early so we can handle requests to
    // toggle the messenger.
    addTemporaryIntercomGlobal();
    services.bootstrap.readyGateCleared(READY_GATE.intercom);
  }

  function init() {
    // Don't bootstrap intercom when testing.
    // TODO: mock out services so we can still test this service.
    if (process.env.NODE_ENV === 'testing') {
      return;
    }

    let cc = services.session.getClientConfig();
    if (get(cc, 'entity.type') !== 'User') {
      // Only enable Intercom for authenticated users
      return;
    }

    const intercomioAppId = services.session.getRegistryValue('intercomioAppId');
    const intercomioUserHash = services.session.getRegistryValue('intercomioUserHash');
    const intercomioEnabled = services.session.getRegistryValue('intercomioEnabled');
    if (intercomioEnabled && intercomioAppId) {
      try {
        addIntercomScript(intercomioAppId);
        startIntercom(intercomioAppId, intercomioUserHash);
      } catch(e) {
        // Prevent intercom issues from failing blue
        services.ga.exceptionTrack('failed to add and start intercom: ' + e);
      }
    }
  }

  // Should be called every time the site is loaded. This essentially starts Intercom so that we can send events and messaging
  function startIntercom(intercomioAppId, intercomioUserHash) {
    const cc = services.session.getClientConfig();
    const entity = cc.entity || {};
    const context = cc.managingContext ? cc.managingContext : cc.context || {};
    let lang = getLanguageCode();
    services.window.Intercom('onUnreadCountChange', emitUnreadCount);
    services.window.Intercom('onHide', onHide);

    let fallback = entity.type === 'User' ? ORPHANED_USER_ORG : undefined;
    // Start (boot) Intercom, passing in the user and org data
    intercomConfig = {
      // We'll manually open and close the messenger
      //custom_launcher_selector: '#posb-intercom',
      hide_default_launcher: true,
      app_id: intercomioAppId,
      user_id: entity.id,
      user_hash: intercomioUserHash,
      language_override: lang,
      company: {
        id: context.id ? context.id : fallback,
        name: context.name,
        organization_type: context.type,
      },
    };
    intercomConfig.name = getDisplayName(entity);

    services.window.Intercom('boot', intercomConfig);

    // Send an event to track the site load (by app id).
    let appId = services.app.getCurrentAppId();
    if (!VIEW_EVENT_APP_EXCLUSIONS.includes(appId)) {
      services.window.Intercom('trackEvent', 'view ' + appId);
    }
  }

  function getLanguageCode() {
    let lang = services.i18n.getLanguageCode();
    // Intercom needs the longer code for Chinese
    if(lang === 'zh') {
      lang = 'zh-CN';
    }
    return lang;
  }

  function getDisplayName(entity) {
    let name = '';
    let displayName = get(entity, 'persona.displayName', null);
    let firstName = get(entity, 'persona.firstName', null);
    let lastName = get(entity, 'persona.lastName', null);

    if (firstName && lastName) {
      name = `${firstName} ${lastName}`;
    }
    else if (displayName) {
      name = displayName;
    }
    else if (firstName) {
      name = firstName;
    }
    else if (lastName) {
      name = lastName;
    }

    return name;
  }

  function hideMessenger() {
    return services.window.Intercom('hide');
  }

  function showMessenger() {
    return services.window.Intercom('show');
  }

  function emitUnreadCount(count) {
    services.window.dispatchEvent(new CustomEvent('posb-intercom-count-updated', {detail: {count}}));
  }

  function onHide() {
    services.window.dispatchEvent(new CustomEvent('posb-intercom-closed', {}));
  }

  function update(userDetails) {
    if (!userDetails) {
      return;
    }

    services.window.Intercom('update', userDetails);
    // Sadly, intercom throttles updates after 10 requests per app load.
    // After 10 requests, it only allows one every 30 minutes.
    // Intercom doesn't give any indication of throttling and simply swallows the request in the client.
    // So, we'll file a GA exception instead.
    if (++numUpdates >= NUM_UPDATES_THROTTLE_THRESHOLD) {
      let user_id = intercomConfig.user_id;
      let company_id = intercomConfig.company.id;

      let details = Object.assign({
        user_id,
        company_id,
      }, userDetails);
      services.ga.exceptionTrack('Intercom update throttled: ' + JSON.stringify(details));
    }
  }

  function eventTrack(eventName, metadata = {}) {
    return services.window.Intercom('trackEvent', eventName, metadata);
  }
}
